import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  LogOut,
} from "lucide-react";
import api from "../services/api";
import usePolling from "../hooks/usePolling";
import { useUser } from "../context/UserContext";
import {
  buildMonteurSidebarSections,
  isNavigationItemActive,
} from "../navigation/navigationConfig";

const MENU_CACHE_TTL_MS = 5 * 60 * 1000;
let menuConfigCache = {
  data: null,
  at: 0,
};

export default function MonteurSidebar({ className = "", onNavigate }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useUser();
  const [menuConfig, setMenuConfig] = useState(defaultMonteurMenuConfig());
  const [badges, setBadges] = useState({
    notificationsUnread: 0,
    leavePending: 0,
    workordersOpen: 0,
    workordersDone: 0,
    opnamesTotal: 0,
  });

  const visibleGroups = useMemo(
    () =>
      buildMonteurSidebarSections({
        user,
        monteurCounters: badges,
        monteurMenuConfig: menuConfig,
      }),
    [badges, menuConfig, user]
  );

  const activeByGroup = useMemo(() => {
    const map = new Map();
    for (const group of visibleGroups) {
      const active = group.items.some((item) => isNavigationItemActive(item, location));
      map.set(group.key, active);
    }
    return map;
  }, [location, visibleGroups]);

  const [expanded, setExpanded] = useState(() => {
    const value = {};
    for (const groupKey of ["werk", "info", "instellingen"]) value[groupKey] = true;
    return value;
  });

  useEffect(() => {
    let active = true;

    async function loadMenuConfig() {
      const ageMs = Date.now() - Number(menuConfigCache.at || 0);
      if (menuConfigCache.data && ageMs >= 0 && ageMs < MENU_CACHE_TTL_MS) {
        setMenuConfig(normalizeMonteurMenuConfig(menuConfigCache.data));
        return;
      }

      try {
        const res = await api.get("/tenant/settings/monteur-menu");
        if (!active) return;
        const next = normalizeMonteurMenuConfig(res?.data?.monteurMenu);
        menuConfigCache = { data: next, at: Date.now() };
        setMenuConfig(next);
      } catch {
        if (!active) return;
        setMenuConfig(defaultMonteurMenuConfig());
      }
    }

    loadMenuConfig();
    return () => {
      active = false;
    };
  }, []);

  usePolling(
    async () => {
      try {
        const [notificationsRes, leaveRes, mineRes, opnamesRes] = await Promise.allSettled([
          api.get("/notifications/mine", { params: { limit: 1, unreadOnly: true } }),
          api.get("/leave-requests/mine"),
          api.get("/werkbonnen/mine"),
          api.get("/opnames"),
        ]);

        const unreadCount =
          notificationsRes.status === "fulfilled"
            ? Number(notificationsRes.value?.data?.unreadCount || 0)
            : 0;
        const leaveItems =
          leaveRes.status === "fulfilled" && Array.isArray(leaveRes.value?.data)
            ? leaveRes.value.data
            : [];
        const leavePending = leaveItems.filter(
          (item) => String(item?.status || "").trim().toLowerCase() === "pending"
        ).length;
        const mineItems =
          mineRes.status === "fulfilled" && Array.isArray(mineRes.value?.data)
            ? mineRes.value.data
            : [];
        const workordersOpen = mineItems.filter((item) => !isDoneWorkorderStatus(item?.status)).length;
        const workordersDone = mineItems.filter((item) => isDoneWorkorderStatus(item?.status)).length;
        const opnamesItems =
          opnamesRes.status === "fulfilled" && Array.isArray(opnamesRes.value?.data)
            ? opnamesRes.value.data
            : [];
        const opnamesTotal = opnamesItems.length;

        setBadges({
          notificationsUnread: Number.isFinite(unreadCount) ? unreadCount : 0,
          leavePending,
          workordersOpen,
          workordersDone,
          opnamesTotal,
        });
      } catch {
        setBadges({
          notificationsUnread: 0,
          leavePending: 0,
          workordersOpen: 0,
          workordersDone: 0,
          opnamesTotal: 0,
        });
      }
    },
    60000,
    []
  );

  function toggleGroup(key) {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function handleLogout() {
    onNavigate?.();
    logout?.();
    navigate("/login", { replace: true });
  }

  return (
    <aside className={`h-full overflow-y-auto border-r bg-white ${className}`.trim()}>
      <div className="p-3">
        {visibleGroups.map((group) => {
          const isOpen = expanded[group.key] || false;
          const groupHasActive = activeByGroup.get(group.key) === true;
          return (
            <section key={group.key} className="mb-3 rounded-lg border border-gray-100">
              <button
                type="button"
                onClick={() => toggleGroup(group.key)}
                className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left"
              >
                <span className={`text-xs font-semibold tracking-wide ${groupHasActive ? "text-blue-700" : "text-gray-500"}`}>
                  {group.title}
                </span>
                {isOpen ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronRight className="h-4 w-4 text-gray-500" />}
              </button>

              {isOpen && (
                <div className="space-y-1 px-2 pb-2">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const badgeCount = resolveBadgeCount(item.to, badges);
                    return (
                      <NavLink
                          key={item.to}
                          to={item.to}
                          onClick={onNavigate}
                          className={({ isActive }) => {
                          const activeItem = isActive || isNavigationItemActive(item, location);
                          return `flex items-center gap-2 rounded-md px-2.5 py-2 text-sm ${
                            activeItem ? "bg-blue-50 text-blue-700" : "text-gray-700 hover:bg-gray-50"
                          }`;
                        }}
                      >
                        <Icon className="h-4 w-4" />
                        <span className="min-w-0 flex-1 truncate">{item.label}</span>
                        {badgeCount > 0 ? (
                          <span className="rounded-full bg-red-600 px-2 py-0.5 text-[11px] font-semibold text-white">
                            {badgeCount > 99 ? "99+" : badgeCount}
                          </span>
                        ) : null}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}

        <section className="mt-4 border-t border-gray-200 pt-3">
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <LogOut className="h-4 w-4" />
            <span>Uitloggen</span>
          </button>
        </section>
      </div>
    </aside>
  );
}

function normalizeMonteurMenuConfig(value) {
  const source = value || {};
  const defaults = defaultMonteurMenuConfig();
  return {
    werk: {
      enabled: source?.werk?.enabled !== false,
      items: {
        ...defaults.werk.items,
        ...(source?.werk?.items || {}),
      },
    },
    info: {
      enabled: source?.info?.enabled !== false,
      items: {
        ...defaults.info.items,
        ...(source?.info?.items || {}),
      },
    },
    instellingen: {
      enabled: source?.instellingen?.enabled !== false,
      items: {
        ...defaults.instellingen.items,
        ...(source?.instellingen?.items || {}),
      },
    },
  };
}

function defaultMonteurMenuConfig() {
  return {
    werk: {
      enabled: true,
      items: {
        werkbonnen: true,
        werkbonnenAfgerond: true,
        werkbonNieuw: true,
        opnames: true,
        siteSurvey: true,
      },
    },
    info: {
      enabled: true,
      items: {
        meldingen: true,
        fotos: true,
        formulieren: true,
        notities: true,
        toolbox: true,
        documenten: true,
      },
    },
    instellingen: {
      enabled: true,
      items: {
        verlof: true,
        weergave: true,
      },
    },
  };
}

function resolveBadgeCount(to, badges) {
  if (to === "/monteur/werkbonnen?status=open") return Number(badges?.workordersOpen || 0);
  if (to === "/monteur/werkbonnen?status=done") return Number(badges?.workordersDone || 0);
  if (to === "/monteur/opnames") return Number(badges?.opnamesTotal || 0);
  if (to === "/monteur/meldingen") return Number(badges?.notificationsUnread || 0);
  if (to === "/monteur/verlof") return Number(badges?.leavePending || 0);
  return 0;
}

function isDoneWorkorderStatus(status) {
  const key = String(status || "").trim().toLowerCase();
  return key === "afgerond" || key === "afgemeld" || key === "gesloten";
}
